import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal, ClipReveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { MagneticButton } from '@/components/site/MagneticButton';
import { CTABand } from '@/components/site/CTABand';
import { WRAP } from '@/components/site/ServicePageParts';
import { SITE, WORK } from '@/data/site';
import { WORK_IMAGES } from '@/data/homeImages';
import { motionOn } from '@/lib/motion';

gsap.registerPlugin(ScrollTrigger);

const EASE = '[transition-timing-function:var(--ease-out)]';

function WorkCard({ w, i, featured }) {
  const img = WORK_IMAGES[w.slug];
  return (
    <article
      id={w.slug}
      data-testid={`work-${w.slug}`}
      className={`work-card group scroll-mt-32 ${featured ? '' : i % 2 === 0 ? 'md:mt-[clamp(80px,10vw,180px)]' : ''}`}
    >
      <ClipReveal className={`relative overflow-hidden rounded-[18px] bg-ink-surface ring-1 ring-white/[0.08] ${featured ? 'aspect-[16/10] md:aspect-[16/8]' : 'aspect-[4/3] md:aspect-[5/6]'}`}>
        <Link
          to={`/work/${w.slug}/`}
          tabIndex={-1}
          aria-hidden
          data-cursor="View"
          className="absolute inset-0 block"
          style={{
            background: `radial-gradient(circle at 30% 20%, ${w.palette[0]}55, transparent 55%), radial-gradient(circle at 80% 80%, ${w.palette[1] || w.palette[0]}40, transparent 50%), #0E0E15`,
          }}
        >
          {img && (
            <span className="work-img absolute -inset-y-[6%] inset-x-0 block">
              <img
                src={img.src}
                alt={img.alt}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
                className={`h-full w-full object-cover object-top saturate-[.85] transition-[transform,filter] [transition-duration:1100ms] ${EASE} group-hover:scale-[1.05] group-hover:saturate-100`}
              />
            </span>
          )}
          <span className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,10,15,.15),transparent_35%,rgba(10,10,15,.88))]" />
          <span className="nd-mono absolute left-3 top-3 sm:left-5 sm:top-5 z-[2] inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[rgba(10,10,15,.6)] backdrop-blur-md border border-white/[0.14] !text-[11px] !text-white">
            <span className="nd-live" />{w.year}
          </span>
          <span className="nd-mono absolute right-3 top-3 sm:right-5 sm:top-5 z-[2] !text-[11px] !text-white/70">
            Case — {String(i + 1).padStart(2, '0')}
          </span>
          <span className="absolute left-5 right-5 bottom-5 sm:left-6 sm:right-6 z-[2] flex items-end justify-between gap-6">
            <span className="nd-mono hidden sm:inline !text-white/70">{w.kind}</span>
            <span className="text-right">
              <b className="block font-display font-medium text-[clamp(40px,5.4vw,96px)] tracking-[-0.05em] leading-none tabular-nums">{w.stat.kpi}</b>
              <span className="text-[13px] text-[#C9C9D3]">{w.stat.label}</span>
            </span>
          </span>
        </Link>
      </ClipReveal>

      <div className={`pt-7 grid grid-cols-1 gap-6 items-start ${featured ? 'lg:grid-cols-[1fr_auto]' : ''}`}>
        <div>
          <div className="nd-mono mb-3">{w.sector}</div>
          <h2 className={`font-display font-semibold tracking-[-0.045em] leading-[0.95] ${featured ? 'text-[clamp(40px,5.6vw,92px)]' : 'text-[clamp(34px,3.8vw,60px)]'}`}>
            <Link to={`/work/${w.slug}/`} className={`inline-block transition-transform [transition-duration:550ms] ${EASE} hover:translate-x-2`}>{w.name}</Link>
          </h2>
          <p className="mt-5 text-ink-muted text-[16px] leading-relaxed max-w-[54ch]">{w.summary}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {w.services.map((sv) => (
              <span key={sv} className="font-mono text-[11px] tracking-[.04em] text-[#C9C9D3] px-2.5 py-1.5 border border-white/[0.14] rounded-md">
                {sv}
              </span>
            ))}
          </div>
        </div>
        <Link
          to={`/work/${w.slug}/`}
          className={`nd-link-u self-start text-white text-[15px] ${featured ? 'lg:mt-8' : ''}`}
          data-testid={`work-cta-${w.slug}`}
        >
          Read the {w.name} case study <span aria-hidden>→</span>
        </Link>
      </div>
    </article>
  );
}

export default function Work() {
  const listRef = useRef(null);
  const [first, ...rest] = WORK;

  // Image parallax inside each clipped frame.
  useEffect(() => {
    if (!motionOn() || !listRef.current) return undefined;
    const ctx = gsap.context(() => {
      gsap.utils.toArray('.work-card').forEach((card) => {
        const img = card.querySelector('.work-img');
        if (!img) return;
        gsap.fromTo(img, { yPercent: -5 }, {
          yPercent: 5,
          ease: 'none',
          scrollTrigger: { trigger: card, start: 'top bottom', end: 'bottom top', scrub: true },
        });
      });
    }, listRef);
    return () => ctx.revert();
  }, []);

  return (
    <div data-testid="work-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-[12vw] -top-[18vw] h-[70vw] w-[70vw] max-h-[1000px] max-w-[1000px] rounded-full blur-[20px]"
          style={{ background: 'radial-gradient(circle at 40% 45%, rgba(108,92,231,.32), rgba(75,159,255,.12) 40%, transparent 68%)' }}
        />
        <div className={`relative ${WRAP} pt-16 md:pt-24 pb-16 md:pb-24`}>
          <nav aria-label="Breadcrumb" className="nd-mono flex flex-wrap items-center gap-2.5 !text-[11px]">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span aria-hidden>/</span>
            <span className="text-white">Work</span>
          </nav>

          <SectionLabel as="h1" num="01" data-testid="work-eyebrow" className="mt-12 md:mt-16">
            Our Work — Websites, Apps &amp; Software We Built
          </SectionLabel>
          <WordReveal
            as="p"
            text="Five live products. Open any of them."
            accent="live"
            data-testid="work-headline"
            className="mt-8 max-w-[14ch] font-display font-semibold text-[clamp(44px,7.4vw,128px)] leading-[0.92] tracking-[-0.05em]"
          />

          <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <Reveal delay={250} className="lg:col-span-5 order-2 lg:order-1">
              <ol className="border-t border-white/[0.08]">
                {WORK.map((w, i) => (
                  <li key={w.slug} className="border-b border-white/[0.08]">
                    <a href={`#${w.slug}`} className="group flex items-center justify-between gap-4 py-3 text-[#C9C9D3] hover:text-white transition-colors">
                      <span className="flex items-baseline gap-4">
                        <span className="nd-mono !text-[11px]">{String(i + 1).padStart(2, '0')}</span>
                        <span className="font-display text-lg tracking-[-0.02em]">{w.name}</span>
                      </span>
                      <span className="nd-mono !text-[11px] hidden sm:inline">{w.kind}</span>
                    </a>
                  </li>
                ))}
              </ol>
            </Reveal>
            <Reveal delay={350} className="lg:col-span-6 lg:col-start-7 order-1 lg:order-2">
              <p className="text-lg md:text-xl text-[#C9C9D3] leading-relaxed max-w-[48ch]">
                Every project here is live and linked. Two are products we built and run ourselves,
                three are client builds. Each has a page with what we built and real screenshots.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <MagneticButton href={SITE.whatsapp} external variant="primary">Book discovery call</MagneticButton>
                <MagneticButton to="/services/" variant="ghost">Our services</MagneticButton>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* LIST */}
      <section ref={listRef} data-testid="work-list" className="relative border-t border-white/[0.08] py-[clamp(72px,9vw,140px)]">
        <div className={WRAP}>
          {first && <WorkCard w={first} i={0} featured />}
          <div className="mt-[clamp(80px,10vw,160px)] grid grid-cols-1 md:grid-cols-2 gap-x-[clamp(24px,4vw,72px)] gap-y-[clamp(72px,9vw,140px)] items-start">
            {rest.map((w, j) => (
              <WorkCard key={w.slug} w={w} i={j + 1} />
            ))}
          </div>
        </div>
      </section>

      <CTABand
        eyebrow="Have something similar in mind?"
        headline="Tell us what you want to build."
        accent="build."
        sub="A free 30-minute call on WhatsApp. We’ll walk you through how these were built and what yours would take."
      />
    </div>
  );
}
